import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageSquare, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { EditSubmission } from "@/components/tam/EditSubmission";
import { api, errMsg, dt } from "@/lib/api";
import { Panel, StageBadge } from "@/components/tam/ui";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STAGES = ["Intro", "Follow Up", "Information Sent", "Qualified Lead", "Quote", "Won", "Not Interested"];
const CHANNELS = [["Call", Phone], ["Email", Mail], ["Visit", MapPin], ["Other", MessageSquare]];
const TASK_STAGES = ["Information Sent", "Qualified Lead", "Quote"];

function LogForm({ accountId, onLogged }) {
  const [channel, setChannel] = useState("Call");
  const [stage, setStage] = useState("Intro");
  const [summary, setSummary] = useState("");
  const [follow, setFollow] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/accounts/${accountId}/activities`, { channel, stage, summary, follow_up_date: follow || null });
      toast.success(TASK_STAGES.includes(stage) ? `Logged – Purfleet task raised for "${stage}"` : "Activity logged");
      setSummary("");
      setFollow("");
      onLogged();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 mb-6" data-testid="activity-form">
      <div className="flex flex-wrap gap-2">
        {CHANNELS.map(([c, Icon]) => (
          <button type="button" key={c} onClick={() => setChannel(c)} data-testid={`channel-${c.toLowerCase()}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition-colors ${channel === c ? "bg-[#0B132B] text-white border-[#0B132B]" : "bg-white hover:bg-slate-50"}`}>
            <Icon size={14} /> {c}
          </button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger data-testid="activity-stage-select"><SelectValue /></SelectTrigger>
          <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s} data-testid={`stage-option-${s.toLowerCase().replace(/\s/g, "-")}`}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={follow} onChange={(e) => setFollow(e.target.value)} data-testid="activity-followup-date" title="Follow-up date (optional)" />
      </div>
      {TASK_STAGES.includes(stage) && <p className="text-xs text-violet-700 bg-violet-50 border border-violet-200 rounded px-3 py-2" data-testid="task-raise-hint">This will raise a task on the Purfleet Manager's task list.</p>}
      <Textarea required rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="What was discussed / sent / requested?" data-testid="activity-summary-input" />
      <Button type="submit" disabled={busy} className="bg-[#D9232A] hover:bg-[#B81920]" data-testid="activity-submit-btn">{busy ? "Logging…" : `Log ${channel}`}</Button>
    </form>
  );
}

export function ActivityPanel({ account, canEdit, onLogged }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(null);
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get(`/accounts/${account.id}/activities`).then((r) => setItems(r.data)), [account.id]);
  useEffect(() => { load(); }, [load]);

  return (
    <Panel title="Log calls, emails & progress" testId="activity-panel">
      {canEdit && <LogForm accountId={account.id} onLogged={() => { load(); onLogged(); }} />}
      <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-3">Activity timeline</h4>
      {items.length === 0 && <p className="text-sm text-slate-500" data-testid="activity-empty">No activity yet.</p>}
      <ol className="border-l-2 border-slate-200 ml-2 space-y-5" data-testid="activity-timeline">
        {items.map((a) => {
          const mine = canEdit && (user.role === "manager" || a.user_id === user.id);
          return (
          <li key={a.id} onClick={() => mine && setEditing(a)} className={`pl-5 relative rounded-md py-1 pr-2 ${mine ? "cursor-pointer hover:bg-slate-50 transition-colors" : ""}`} data-testid={`activity-item-${a.id}`}>
            <span className="absolute -left-[7px] top-2.5 w-3 h-3 rounded-full bg-[#D9232A]" />
            <div className="flex flex-wrap items-center gap-2"><StageBadge stage={a.stage} /><span className="text-xs font-semibold">{a.channel}</span>
              {a.follow_up_date && <span className="text-xs text-slate-500">· follow up {a.follow_up_date}</span>}
              {mine && <span className="ml-auto text-xs text-slate-400 flex items-center gap-1"><Pencil size={12} /> Update</span>}</div>
            <p className="text-sm mt-1.5 whitespace-pre-wrap">{a.summary}</p>
            <p className="text-xs text-slate-500 mt-1 font-mono">{a.user_name} · {dt(a.created_at)}{a.edits?.length ? ` · edited ${a.edits.length}x (last ${dt(a.updated_at)})` : ""}</p>
          </li>
          );
        })}
      </ol>
      <EditSubmission activity={editing} onClose={() => setEditing(null)} onSaved={() => { load(); onLogged(); }} />
    </Panel>
  );
}
